import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { getApiUrl } from '@/lib/utils';

// Define the context type
type AuthContextType = {
  session: Session | null;
  user: User | null;
  clientData: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any | null; data: any | null }>;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any | null; data: any | null }>;
  signOut: () => Promise<void>;
};

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  clientData: null,
  loading: true,
  signIn: async () => ({ error: null, data: null }),
  signUp: async () => ({ error: null, data: null }),
  signOut: async () => {},
});

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Safe logging function that redacts sensitive information
const safeLog = (message: string, data: any = {}) => {
  // Create a copy of the data to avoid modifying the original
  const sanitizedData = { ...data };
  
  // List of keys that might contain sensitive information
  const sensitiveKeys = [
    'password', 'token', 'access_token', 'refresh_token', 'userData', 
    'email', 'phone', 'address', 'shopAddress', 'name', 'user_metadata',
    'app_metadata', 'session', 'user'
  ];
  
  // Redact sensitive information
  Object.keys(sanitizedData).forEach(key => {
    if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
      sanitizedData[key] = '[REDACTED]';
    } else if (typeof sanitizedData[key] === 'object' && sanitizedData[key] !== null) {
      // Recursively sanitize nested objects
      sanitizedData[key] = '[OBJECT]';
    }
  });
  
  // Only log in development environment
  if (import.meta.env.DEV) {
    console.log(`[Auth] ${message}`, sanitizedData);
  }
};

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [clientData, setClientData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to sync user data with client table
  const syncUserWithClientTable = async (userId: string, userData: any, isNewUser = false) => {
    try {
      // Safe logging
      safeLog('Syncing user with client table', { userId, isNewUser });
      
      if (!import.meta.env.VITE_API_BASE_URL || !import.meta.env.VITE_API_TOKEN || !import.meta.env.VITE_CLIENTS_TABLE) {
        console.error('Missing required environment variables for client table sync:', {
          hasApiBaseUrl: !!import.meta.env.VITE_API_BASE_URL,
          hasApiToken: !!import.meta.env.VITE_API_TOKEN,
          hasClientsTable: !!import.meta.env.VITE_CLIENTS_TABLE
        });
        return { error: 'Configuration error', data: null };
      }

      // Get properly formatted API URL
      const apiUrl = getApiUrl(import.meta.env.VITE_API_BASE_URL);
      const clientsTable = import.meta.env.VITE_CLIENTS_TABLE;
      const apiToken = import.meta.env.VITE_API_TOKEN;
      
      safeLog('API configuration', { apiUrl, clientsTable, hasToken: !!apiToken });

      // For new users, create a record in the client table
      if (isNewUser) {
        safeLog('Creating new user record in client table');
        
        // Make sure we have all required fields for the client table
        const clientData = {
          ...userData,
          supabaseUserId: userId,
          isActive: true
        };
        
        // Don't log client data as it contains sensitive information
        
        try {
          const response = await fetch(`${apiUrl}/tables/${clientsTable}/records`, {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
              'xc-token': apiToken
            },
            body: JSON.stringify(clientData),
          });
          
          const responseStatus = response.status;
          const responseOk = response.ok;
          
          safeLog('Create user API response status', { status: responseStatus, ok: responseOk });

          if (!responseOk) {
            const responseText = await response.text();
            throw new Error(`Failed to create client record: ${responseStatus}`);
          }

          let data;
          try {
            data = JSON.parse(await response.text());
          } catch (e) {
            console.error('Failed to parse API response');
            throw new Error('Invalid API response format');
          }
          
          safeLog('Successfully created user in client table');
          return { error: null, data };
        } catch (error) {
          console.error('Error creating user in client table');
          throw error;
        }
      } 
      // For existing users, find and update their record
      else {
        safeLog('Looking for existing user in client table by supabaseUserId');
        
        try {
          // Get all records and filter manually
          const response = await fetch(`${apiUrl}/tables/${clientsTable}/records`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              'xc-token': apiToken
            }
          });
          
          if (!response.ok) {
            console.error('Failed to fetch client records:', {
              status: response.status
            });
            throw new Error(`Failed to fetch client records: ${response.status}`);
          }
          
          const data = await response.json();
          safeLog('Records count', { count: data.list?.length || 0 });
          
          // Manually filter the records to find the user by supabaseUserId
          const userRecord = data.list?.find(record => record.supabaseUserId === userId);
          
          // If user exists in client table, return that data
          if (userRecord) {
            safeLog('Found user in client table by supabaseUserId');
            return { error: null, data: userRecord };
          }
          
          // If user doesn't exist by supabaseUserId but has email, try to find by email
          if (userData.email) {
            safeLog('User not found by supabaseUserId, looking for email');
            
            // Manually filter to find by email
            const emailRecord = data.list?.find(record => record.email === userData.email);
            
            if (emailRecord) {
              safeLog('Found user by email, updating with supabaseUserId');
              
              // Update existing record with supabaseUserId
              const updateResponse = await fetch(`${apiUrl}/tables/${clientsTable}/records`, {
                method: 'PATCH',
                headers: {
                  'accept': 'application/json',
                  'Content-Type': 'application/json',
                  'xc-token': apiToken
                },
                body: JSON.stringify({
                  Id: emailRecord.Id,
                  supabaseUserId: userId
                }),
              });
              
              if (!updateResponse.ok) {
                console.error('Failed to update user record:', {
                  status: updateResponse.status
                });
                throw new Error(`Failed to update user record: ${updateResponse.status}`);
              }
              
              const updatedData = await updateResponse.json();
              safeLog('Successfully updated user with supabaseUserId');
              return { error: null, data: updatedData };
            }
            
            safeLog('User not found by email either, creating new record');
            // If no matching record found, create a new one
            return await syncUserWithClientTable(userId, userData, true);
          }
          
          safeLog('No email provided to search for existing user');
          return { error: 'User not found in client table', data: null };
        } catch (error) {
          console.error('Error finding or updating user in client table');
          throw error;
        }
      }
    } catch (error) {
      console.error('Error syncing user with client table');
      return { error, data: null };
    }
  };

  // Function to fetch client data
  const fetchClientData = async (userId: string) => {
    try {
      safeLog('Fetching client data for user', { userId });
      
      // Get user email from Supabase if not available in state
      let userEmail = user?.email;
      if (!userEmail) {
        try {
          // Try to get the user from Supabase
          const { data, error } = await supabase.auth.getUser();
          
          // If there's an error (like 403 Forbidden)
          if (error) {
            console.error('Error getting user from Supabase:', error.message);
            
            // Try to refresh the session
            safeLog('Attempting to refresh session');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.error('Failed to refresh session:', refreshError.message);
              return null;
            }
            
            if (refreshData.session) {
              safeLog('Session refreshed successfully');
              setSession(refreshData.session);
              setUser(refreshData.session.user);
              userEmail = refreshData.session.user.email;
            } else {
              console.error('Session refresh returned no session');
              return null;
            }
          } else {
            userEmail = data.user?.email;
            safeLog('Retrieved email from Supabase');
          }
        } catch (authError) {
          console.error('Exception during Supabase auth operation:', authError);
          return null;
        }
      }
      
      if (!userEmail) {
        console.error('No email available for user, cannot fetch client data');
        return null;
      }
      
      const { error, data } = await syncUserWithClientTable(userId, { email: userEmail });
      if (error) {
        console.error('Error fetching client data:', error);
        return null;
      }
      safeLog('Successfully fetched client data');
      return data;
    } catch (error) {
      console.error('Error in fetchClientData:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        safeLog('Initializing auth state');
        
        // Get initial session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting initial session:', sessionError.message);
          // Try to refresh the session if there was an error
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            console.error('Failed to refresh session during initialization:', refreshError.message);
            // Clear any potentially invalid session data
            localStorage.removeItem('supabase.auth.token');
            setSession(null);
            setUser(null);
            return;
          }
          
          if (refreshData.session) {
            safeLog('Session refreshed successfully during initialization');
            setSession(refreshData.session);
            setUser(refreshData.session.user ?? null);
          }
        } else {
          const initialSession = sessionData.session;
          safeLog('Initial session retrieved', { hasSession: !!initialSession });
          
          setSession(initialSession);
          setUser(initialSession?.user ?? null);

          if (initialSession?.user) {
            safeLog('User is already logged in, fetching client data');
            const clientData = await fetchClientData(initialSession.user.id);
            setClientData(clientData);
          }
        }

        // Set up auth state change listener
        const { data: { subscription } } = await supabase.auth.onAuthStateChange(
          async (event, newSession) => {
            safeLog('Auth state changed', { event: event, hasSession: !!newSession });
            
            // Handle different auth events
            switch (event) {
              case 'SIGNED_IN':
                safeLog('User signed in');
                if (newSession) {
                  setSession(newSession);
                  setUser(newSession.user ?? null);
                  
                  if (newSession.user) {
                    const clientData = await fetchClientData(newSession.user.id);
                    setClientData(clientData);
                    
                    // Update localStorage with client ID if available
                    if (clientData?.Id) {
                      localStorage.setItem('clientId', clientData.Id.toString());
                      localStorage.setItem('email', newSession.user.email || '');
                    }
                  }
                }
                break;
                
              case 'SIGNED_OUT':
                safeLog('User signed out');
                setSession(null);
                setUser(null);
                setClientData(null);
                localStorage.removeItem('clientId');
                localStorage.removeItem('email');
                break;
                
              case 'TOKEN_REFRESHED':
                safeLog('Token refreshed');
                if (newSession) {
                  setSession(newSession);
                  setUser(newSession.user ?? null);
                }
                break;
                
              case 'USER_UPDATED':
                safeLog('User updated');
                if (newSession) {
                  setSession(newSession);
                  setUser(newSession.user ?? null);
                }
                break;
                
              case 'PASSWORD_RECOVERY':
                safeLog('Password recovery event detected');
                
                // Make sure we have the session set correctly
                if (newSession) {
                  setSession(newSession);
                  setUser(newSession.user ?? null);
                  
                  // Ensure the token is properly set in Supabase
                  if (newSession.access_token) {
                    try {
                      safeLog('Setting session with access token from PASSWORD_RECOVERY event');
                      await supabase.auth.setSession({
                        access_token: newSession.access_token,
                        refresh_token: newSession.refresh_token || '',
                      });
                      safeLog('Session set successfully for password recovery');
                      
                      // After password reset, explicitly fetch and update client data
                      if (newSession.user) {
                        safeLog('Password reset successful, re-syncing client data');
                        const clientData = await fetchClientData(newSession.user.id);
                        
                        if (clientData) {
                          setClientData(clientData);
                          localStorage.setItem('clientId', clientData.Id.toString());
                          localStorage.setItem('email', newSession.user.email || '');
                          safeLog('Client data updated after password reset');
                        }
                      }
                    } catch (error) {
                      console.error('Error setting session during password recovery:', error);
                    }
                  }
                }
                break;
                
              default:
                safeLog(`Unhandled auth event: ${event}`);
                if (newSession) {
                  setSession(newSession);
                  setUser(newSession.user ?? null);
                }
                break;
            }
          }
        );

        setLoading(false);
        safeLog('Auth initialization complete');

        // Cleanup subscription on unmount
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing auth state:', error);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      safeLog('Signing in user');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Supabase sign in error');
        return { error, data: null };
      }

      safeLog('Supabase sign in successful', { hasUser: !!data.user });

      if (data.user) {
        safeLog('Fetching client data for signed in user');
        const clientData = await fetchClientData(data.user.id);
        setClientData(clientData);

        // Store session data in localStorage for compatibility with existing code
        localStorage.setItem("sessionToken", data.session?.access_token || '');
        localStorage.setItem("sessionExpiry", String(Date.now() + (24 * 60 * 60 * 1000))); // 24 hours
        localStorage.setItem("clientId", clientData?.Id?.toString() || '');
        localStorage.setItem("lastLoginTime", String(Date.now()));
        localStorage.setItem("email", email);
        
        safeLog('Session data stored in localStorage');
      }

      return { error: null, data };
    } catch (error) {
      console.error('Error signing in');
      return { error, data: null };
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string, userData: any) => {
    try {
      safeLog('Signing up new user');
      
      // First check if required environment variables are available
      if (!import.meta.env.VITE_API_BASE_URL || !import.meta.env.VITE_API_TOKEN || !import.meta.env.VITE_CLIENTS_TABLE) {
        console.error('Missing required environment variables for registration:', {
          hasApiBaseUrl: !!import.meta.env.VITE_API_BASE_URL,
          hasApiToken: !!import.meta.env.VITE_API_TOKEN,
          hasClientsTable: !!import.meta.env.VITE_CLIENTS_TABLE
        });
        return { error: new Error('Application configuration error. Please contact support.'), data: null };
      }
      
      // Get properly formatted API URL
      const apiUrl = getApiUrl(import.meta.env.VITE_API_BASE_URL);
      const clientsTable = import.meta.env.VITE_CLIENTS_TABLE;
      const apiToken = import.meta.env.VITE_API_TOKEN;
      
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase();
      
      // Get the app URL for Supabase redirects
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      console.log(`Using app URL for redirects: ${appUrl}`);
      
      // First check if a user with this email already exists in the clients table
      try {
        console.log('Checking if email already exists in clients table');
        const checkResponse = await fetch(`${apiUrl}/tables/${clientsTable}/records?where=(email,eq,${encodeURIComponent(normalizedEmail)})`, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'xc-token': apiToken,
            'User-Agent': 'wedding-orderly-client'
          }
        });
        
        if (checkResponse.ok) {
          const existingRecords = await checkResponse.json();
          if (existingRecords?.list?.length > 0) {
            console.log(`Found ${existingRecords.list.length} existing records with email: ${normalizedEmail}`);
            
            // Check if any of the existing records have a name (complete record)
            const completeRecord = existingRecords.list.find(record => record.name);
            
            if (completeRecord) {
              // If a complete record exists, return an error
              return { error: new Error('An account with this email already exists. Please login instead.'), data: null };
            }
            
            // If only empty records exist, delete them before proceeding
            console.log('Found only empty records, cleaning up before registration');
            for (const record of existingRecords.list) {
              try {
                // Delete the empty record
                const deleteResponse = await fetch(`${apiUrl}/tables/${clientsTable}/records`, {
                  method: 'DELETE',
                  headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json',
                    'xc-token': apiToken,
                    'User-Agent': 'wedding-orderly-client'
                  },
                  body: JSON.stringify({
                    Id: record.Id
                  })
                });
                
                if (deleteResponse.ok) {
                  console.log(`Successfully deleted empty record with ID: ${record.Id}`);
                } else {
                  console.error(`Failed to delete empty record with ID: ${record.Id}`);
                }
              } catch (deleteError) {
                console.error(`Error deleting empty record with ID: ${record.Id}:`, deleteError);
              }
            }
          }
        }
      } catch (checkError) {
        console.error('Error checking for existing user:', checkError);
        // Continue with registration process even if check fails
      }
      
      // Register user with Supabase Auth
      console.log('Registering user with Supabase Auth');
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone,
            shopAddress: userData.shopAddress,
          },
          emailRedirectTo: `${appUrl}/login`,
        },
      });

      if (error) {
        console.error('Supabase sign up error:', error);
        return { error, data: null };
      }

      safeLog('Supabase sign up successful', { hasUser: !!data.user });

      if (!data.user) {
        console.error('Supabase sign up succeeded but no user data was returned');
        return { error: new Error('Registration failed: No user data returned'), data: null };
      }
      
      // Wait a moment to ensure any automatic processes have completed
      console.log('Waiting for any automatic processes to complete...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if Supabase has already created a record
      console.log('Checking if Supabase has already created a record');
      let existingRecords = [];
      
      try {
        const checkResponse = await fetch(`${apiUrl}/tables/${clientsTable}/records?where=(email,eq,${encodeURIComponent(normalizedEmail)})`, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'xc-token': apiToken,
            'User-Agent': 'wedding-orderly-client'
          }
        });
        
        if (checkResponse.ok) {
          const response = await checkResponse.json();
          existingRecords = response.list || [];
          console.log(`Found ${existingRecords.length} records with email: ${normalizedEmail} after Supabase registration`);
        }
      } catch (checkError) {
        console.error('Error checking for records after Supabase registration:', checkError);
      }
      
      // Delete any empty records that might have been created
      for (const record of existingRecords) {
        // Skip records with names (complete records)
        if (record.name) continue;
        
        try {
          console.log(`Deleting empty record with ID: ${record.Id}`);
          const deleteResponse = await fetch(`${apiUrl}/tables/${clientsTable}/records`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
              'xc-token': apiToken,
              'User-Agent': 'wedding-orderly-client'
            },
            body: JSON.stringify({
              Id: record.Id
            })
          });
          
          if (deleteResponse.ok) {
            console.log(`Successfully deleted empty record with ID: ${record.Id}`);
          } else {
            console.error(`Failed to delete empty record with ID: ${record.Id}`);
          }
        } catch (deleteError) {
          console.error(`Error deleting empty record with ID: ${record.Id}:`, deleteError);
        }
      }
      
      // Create a complete client record
      console.log('Creating complete client record');
      
      // Prepare client data
      const clientData = {
        name: userData.name,
        email: normalizedEmail,
        phone: userData.phone,
        shopAddress: userData.shopAddress,
        supabaseUserId: data.user.id,
        isActive: true
      };
      
      try {
        const response = await fetch(`${apiUrl}/tables/${clientsTable}/records`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            'xc-token': apiToken,
            'User-Agent': 'wedding-orderly-client'
          },
          body: JSON.stringify(clientData),
        });
        
        if (!response.ok) {
          const responseText = await response.text();
          console.error('Failed to create client record:', {
            status: response.status,
            text: responseText
          });
          
          return { 
            error: new Error(`Failed to create client record: ${response.status}`), 
            data: null 
          };
        }
        
        // Parse the response
        const responseData = await response.json();
        console.log('Client record created successfully:', { 
          clientId: responseData.Id,
          email: responseData.email
        });
        
        // Store client data in state
        setClientData(responseData);
        
        // Store session data in localStorage
        localStorage.setItem("clientId", responseData.Id.toString());
        localStorage.setItem("email", normalizedEmail);
        
        console.log('Registration complete, localStorage updated with clientId:', responseData.Id);
        
        return { error: null, data: { user: data.user, client: responseData } };
      } catch (createError) {
        console.error('Error creating client record:', createError);
        return { error: createError, data: null };
      }
    } catch (error) {
      console.error('Error during registration process:', error);
      return { error, data: null };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      safeLog('Signing out user');
      await supabase.auth.signOut();
      
      // Clear localStorage data
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("sessionExpiry");
      localStorage.removeItem("clientId");
      localStorage.removeItem("lastLoginTime");
      localStorage.removeItem("email");
      
      safeLog('User signed out, localStorage cleared');
    } catch (error) {
      console.error('Error signing out');
    }
  };

  // Provide the auth context to children
  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        clientData,
        loading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
